import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "./services/supabase";
import { useAuth } from "./context/AuthContext";
import { getUserRestaurants } from "./services/menuService";
import { FiPlus, FiEdit3, FiTrash2, FiGrid, FiShoppingBag, FiBarChart2, FiCamera } from "react-icons/fi";
import styles from "./Dashboard.module.css";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectedAccountId, setConnectedAccountId] = useState(null);
  const [accountStatus, setAccountStatus] = useState(null);
  
  // Add state for logo management
  const [logoModal, setLogoModal] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (user) {
        try {
          console.log("Loading dashboard data for user:", user.id);
          
          // Get Stripe Connect ID and account status - still needed for setup notification
          const { data: profile } = await supabase
            .from('user_profiles')
            .select('stripe_connect_id, stripe_account_status')
            .eq('user_id', user.id)
            .single();
          
          if (profile && profile.stripe_connect_id) {
            setConnectedAccountId(profile.stripe_connect_id);
            setAccountStatus(profile.stripe_account_status || 'pending');
            
            // Still verify status with Stripe API to ensure proper setup notification
            try {
              const response = await fetch('/check-account-type', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ account_id: profile.stripe_connect_id })
              });
              
              if (response.ok) {
                const accountData = await response.json();
                const newStatus = (accountData.charges_enabled && accountData.payouts_enabled) 
                  ? 'active' : 'pending';
                setAccountStatus(newStatus);
                
                // Update database if status changed
                if (newStatus !== profile.stripe_account_status) {
                  await supabase
                    .from('user_profiles')
                    .update({ stripe_account_status: newStatus })
                    .eq('user_id', user.id);
                }
              }
            } catch (err) {
              console.error("Error checking account with Stripe:", err);
            }
          }
          
          // Get user's restaurants
          const restaurants = await getUserRestaurants(user.id);
          setRestaurants(restaurants);
        } catch (error) {
          console.error("Error loading dashboard data:", error);
        } finally {
          setLoading(false);
        }
      }
    };
    
    loadData();
  }, [user]);

  // Add logo upload function
  const handleLogoUpload = async (restaurantId, file) => {
    if (!file) return;
    
    setUploadingLogo(true);
    try {
      // Create unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `restaurant-logos/${restaurantId}-${Date.now()}.${fileExt}`;
      
      // Upload to Supabase storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('restaurant-assets')
        .upload(fileName, file);
        
      if (uploadError) throw uploadError;
      
      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('restaurant-assets')
        .getPublicUrl(fileName);
        
      // Update restaurant record
      const { error: updateError } = await supabase
        .from('restaurants')
        .update({ logo_url: publicUrl })
        .eq('id', restaurantId);
        
      if (updateError) throw updateError;
      
      // Update local state
      setRestaurants(restaurants.map(restaurant => 
        restaurant.id === restaurantId 
          ? { ...restaurant, logo_url: publicUrl }
          : restaurant
      ));
      
      setLogoModal(null);
    } catch (error) {
      console.error('Error uploading logo:', error);
      alert('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteRestaurant = async (restaurantId) => {
    if (window.confirm("Are you sure you want to delete this restaurant? This action cannot be undone.")) {
      try {
        // Delete all menu items and categories first (cascading delete)
        const { error } = await supabase
          .from('restaurants')
          .delete()
          .eq('id', restaurantId);
          
        if (error) throw error;
        
        setRestaurants(restaurants.filter(r => r.id !== restaurantId));
      } catch (error) {
        console.error("Error deleting restaurant:", error);
      }
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h1 className={styles.pageTitle}>Dashboard</h1>
        <p className={styles.welcomeText}>
          Welcome{user?.email ? `, ${user.email.split('@')[0]}` : ''}!
        </p>
        
        {/* Keep the Stripe setup notification if needed */}
        {(!connectedAccountId || accountStatus !== 'active') && (
          <div className={styles.infoCallout}>
            <p className={styles.infoCalloutText}>
              {connectedAccountId ? 
                'Please complete your Stripe account setup to accept payments' : 
                'Please connect your account with Stripe to accept payments'
              }
            </p>
            <button 
              onClick={() => navigate("/")}
              className={styles.primaryButton}
            >
              {connectedAccountId ? 'Complete Stripe Setup' : 'Set up Stripe Connect'}
            </button>
          </div>
        )}
        
        <div className={styles.dashboardSection}>
          <h2 className={styles.sectionTitle}>Your Restaurants</h2>
          <p className={styles.sectionDescription}>
            Manage your restaurant menus, QR codes, and orders.
          </p>
        </div>
        
        {restaurants.length === 0 ? (
          <div className={styles.emptyState}>
            <h3>You don't have any restaurants yet</h3>
            <p>Create your first restaurant to get started.</p>
            <button 
              onClick={() => navigate('/restaurant/new')}
              className={styles.primaryButton}
            >
              <FiPlus className={styles.buttonIcon} />
              Add Restaurant
            </button>
          </div>
        ) : (
          <>
            <div className={styles.restaurantGrid}>
              {restaurants.map(restaurant => (
                <div key={restaurant.id} className={styles.restaurantCard}>
                  {/* Add logo section */}
                  <div className={styles.restaurantLogoSection}>
                    <div className={styles.logoThumbnail}>
                      {restaurant.logo_url ? (
                        <img 
                          src={restaurant.logo_url} 
                          alt={`${restaurant.name} logo`}
                          className={styles.logoImage}
                        />
                      ) : (
                        <div className={styles.logoPlaceholder}>
                          <FiCamera size={24} />
                        </div>
                      )}
                      <button 
                        className={styles.logoEditButton}
                        onClick={() => setLogoModal(restaurant.id)}
                        title="Change logo"
                      >
                        <FiEdit3 size={12} />
                      </button>
                    </div>
                  </div>
                  
                  <h3 className={styles.restaurantName}>{restaurant.name}</h3>
                  <p className={styles.restaurantDesc}>
                    {restaurant.description || 'No description available'}
                  </p>
                  
                  <div className={styles.cardActions}>
                    <button 
                      onClick={() => navigate(`/restaurant/${restaurant.id}/analytics`)}
                      className={styles.viewAnalyticsButton}
                    >
                      <FiBarChart2 size={16} />
                      View Analytics
                    </button>
                  </div>
                  
                  <div className={styles.actionButtons}>
                    <button 
                      onClick={() => navigate(`/restaurant/${restaurant.id}/menu`)}
                      className={styles.editButton}
                    >
                      <FiEdit3 size={16} />
                      Edit Menu
                    </button>
                    
                    <button 
                      onClick={() => navigate(`/restaurant/${restaurant.id}/qr`)}
                      className={styles.qrButton}
                    >
                      <FiGrid size={16} />
                      QR Codes
                    </button>
                    
                    <button 
                      onClick={() => navigate(`/restaurant/${restaurant.id}/orders`)}
                      className={styles.ordersButton}
                    >
                      <FiShoppingBag size={16} />
                      Orders
                    </button>
                    
                    <button 
                      onClick={() => navigate(`/restaurant/${restaurant.id}/analytics`)}
                      className={styles.analyticsButton}
                    >
                      <FiBarChart2 size={16} />
                      Analytics
                    </button>
                    
                    <button 
                      onClick={() => handleDeleteRestaurant(restaurant.id)}
                      className={styles.deleteButton}
                    >
                      <FiTrash2 size={16} />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
            
            <button 
              onClick={() => navigate('/restaurant/new')} 
              className={styles.addButton}
              aria-label="Add new restaurant"
            >
              <FiPlus />
            </button>
          </>
        )}
      </div>

      {/* Add the logo upload modal */}
      {logoModal && (
        <div className={styles.logoModal}>
          <div className={styles.logoModalContent}>
            <h3>Update Restaurant Logo</h3>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  handleLogoUpload(logoModal, file);
                }
              }}
              disabled={uploadingLogo}
              className={styles.fileInput}
            />
            <div className={styles.logoModalActions}>
              <button 
                onClick={() => setLogoModal(null)}
                className={styles.cancelButton}
                disabled={uploadingLogo}
              >
                Cancel
              </button>
            </div>
            {uploadingLogo && <p>Uploading...</p>}
          </div>
        </div>
      )}
    </div>
  );
}